import useApi from "@/hooks/useApi";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconClose1 } from "@velaro/velaro-component-lib";
import LoadingSpinner from "@/components/LoadingSpinner";
import AccelaAccounts from "./AccelaAccounts";
import AccelaDataMapping from "./AccelaDataMapping";
import AccelaLog from "./AccelaLog";
import IntegrationActivityLog from "../IntegrationActivityLog";
import StarterWorkflowWizard from "../StarterWorkflowWizard";
import useSelectedTeam from "@/hooks/useSelectedTeam";
import SkillControlsPanel from "../SkillControlsPanel";

interface AccelaConfig {
  id: number;
  agencyName: string;
  clientId: string;
  clientSecret: string;
  emailAddress: string;
  accessToken: string;
  refreshToken: string;
  scope?: string;
  teamId: number;
  token?: string;
  refreshTokenExpired?: boolean;
}

// ─── Capabilities ─────────────────────────────────────────────────────────────

const CAPABILITIES = [
  {
    title: "Permits & licensing",
    icon: "📋",
    items: [
      "Search any permit or license record by address, applicant name, parcel number, or record number — instant lookup without logging into Accela",
      "Retrieve full permit details: status, type, assigned inspector, fees due, conditions, and workflow stage",
      "Citizens can ask 'what's the status of my building permit?' and the AI answers with live Accela data",
      "accela_get_record — the AI checks permit status automatically; agents never need to search Accela manually",
      "accela_create_record — submit new permit or license applications directly from a chat conversation"
    ]
  },
  {
    title: "Inspections",
    icon: "🔍",
    items: [
      "Look up all scheduled and completed inspections on any permit record — inspector name, date, status, and result",
      "Schedule a new inspection for a permit directly from chat — citizen never has to call the agency",
      "accela_schedule_inspection — AI books inspections in Accela without any human intervention",
      "Retrieve available inspection types for a record type — so the AI always presents valid options",
      "Citizens can ask 'when is my inspection?' and get a real-time answer from Accela"
    ]
  },
  {
    title: "311 & citizen service requests",
    icon: "🏙️",
    items: [
      "Citizens submit 311-style service requests (potholes, broken streetlights, graffiti, noise) directly from the chat widget",
      "accela_create_record with service request type — AI captures issue type, location, and description, then creates the record",
      "Citizens can follow up: 'what's the status of my pothole report?' — AI looks it up by record number or address",
      "accela_search_records_by_address — find all open service requests near a given address",
      "Supports any service request type configured in Accela — parks, utilities, code enforcement, public works"
    ]
  },
  {
    title: "Contacts & professionals",
    icon: "👤",
    items: [
      "Search licensed professionals (contractors, engineers, architects) by name or license number — verify license status, expiry, and type",
      "Look up citizen contacts — retrieve all permits and service requests associated with an address or contact",
      "Create and update contact records in Accela from information collected in the chat",
      "accela_search_professionals — AI can verify contractor licensing before citizens hire them",
      "accela_get_contact_licenses — AI retrieves all active licenses for a professional on demand"
    ]
  },
  {
    title: "Parcel & address lookup",
    icon: "🗺️",
    items: [
      "Look up any parcel by APN or address — returns owner name, zoning, land use, and assessor data from Accela",
      "accela_get_parcel_info — AI can answer 'what's the zoning at 123 Main St?' or 'who owns this parcel?'",
      "Find all open permits and service requests associated with a parcel — full history in one call",
      "Supports address search and cross-referencing between parcel data and permit records"
    ]
  },
  {
    title: "AI workflow tools",
    icon: "🤖",
    items: [
      "30 tools available across Records, Inspections, 311/Service Requests, Contacts, and Agency/Lookup categories",
      "No workflow configuration required — the AI decides when to call Accela based on conversation context",
      "Supports all Accela record types: permits, licenses, service requests, code enforcement, business licenses",
      "Works with Accela Cloud (SaaS) and Accela On-Premise deployments via the v4 REST API",
      "Each tool is gated by the EnableAccela subscription flag — off by default, enabled per tenant"
    ]
  }
];

// ─── AI Tools ─────────────────────────────────────────────────────────────────

const AI_TOOLS = [
  // Records
  { group: "Records", name: "accela_search_records", label: "Search records", description: "Search permits, licenses, or service requests by type, status, address, or applicant name." },
  { group: "Records", name: "accela_get_record", label: "Get record details", description: "Retrieve full details of a permit or license record — status, type, conditions, fees, and workflow stage." },
  { group: "Records", name: "accela_create_record", label: "Create record", description: "Submit a new permit application, license application, or 311 service request directly from chat." },
  { group: "Records", name: "accela_update_record", label: "Update record", description: "Update fields on an existing Accela record — description, status notes, or custom fields." },
  { group: "Records", name: "accela_get_record_status", label: "Get record status", description: "Get the current processing status and workflow stage for any permit or service request." },
  { group: "Records", name: "accela_get_record_conditions", label: "Get record conditions", description: "Retrieve all conditions of approval or hold conditions attached to a permit record." },
  { group: "Records", name: "accela_get_record_documents", label: "Get record documents", description: "List all documents attached to a permit or license record — plans, reports, certificates." },
  { group: "Records", name: "accela_get_record_workflow", label: "Get record workflow", description: "Retrieve the workflow tasks and their completion status for a permit record." },
  { group: "Records", name: "accela_get_record_contacts", label: "Get record contacts", description: "Get all contacts associated with a permit — applicant, owner, contractor, engineer." },
  { group: "Records", name: "accela_search_by_address", label: "Search by address", description: "Find all open permits and service requests at a given address or parcel." },
  // Inspections
  { group: "Inspections", name: "accela_get_record_inspections", label: "Get inspections", description: "List all scheduled and completed inspections on a permit — inspector name, date, and result." },
  { group: "Inspections", name: "accela_schedule_inspection", label: "Schedule inspection", description: "Book a new inspection on a permit record directly from chat. Citizen never has to call the agency." },
  { group: "Inspections", name: "accela_get_inspection_types", label: "Get inspection types", description: "Retrieve available inspection types for a given record type — so the AI presents valid options." },
  // Contacts
  { group: "Contacts", name: "accela_search_contacts", label: "Search contacts", description: "Search Accela contacts by name, email, or license number." },
  { group: "Contacts", name: "accela_get_contact", label: "Get contact details", description: "Retrieve full contact record — addresses, licenses, and associated permits." },
  { group: "Contacts", name: "accela_create_contact", label: "Create contact", description: "Create a new contact record in Accela from information collected in the conversation." },
  { group: "Contacts", name: "accela_update_contact", label: "Update contact", description: "Update an existing contact record — address, email, or phone number." },
  { group: "Contacts", name: "accela_get_contact_records", label: "Get contact records", description: "Retrieve all permits and service requests linked to a contact." },
  { group: "Contacts", name: "accela_get_contact_addresses", label: "Get contact addresses", description: "Get all addresses associated with a contact record." },
  { group: "Contacts", name: "accela_get_contact_licenses", label: "Get contact licenses", description: "Retrieve all active and expired licenses for a professional contact." },
  // Agency & Lookup
  { group: "Agency & Lookup", name: "accela_get_record_types", label: "Get record types", description: "Retrieve the list of available permit and license record types configured for the agency." },
  { group: "Agency & Lookup", name: "accela_get_agency_info", label: "Get agency info", description: "Returns agency name, contact info, hours, and office locations from Accela configuration." },
  { group: "Agency & Lookup", name: "accela_search_professionals", label: "Search licensed professionals", description: "Verify contractor, engineer, or architect license status — name, expiry, and license type." },
  { group: "Agency & Lookup", name: "accela_get_parcel_info", label: "Get parcel info", description: "Look up a parcel by APN or address — owner, zoning, land use, and assessor data." },
  // 311 / Citizen Service Requests (PublicStuff-equivalent)
  { group: "311 / Citizen Services", name: "accela_create_service_request", label: "Submit 311 service request", description: "Citizen submits a municipal issue (pothole, broken streetlight, graffiti, abandoned vehicle, noise) — AI captures type, location, and description, creates the Accela service request record." },
  { group: "311 / Citizen Services", name: "accela_get_service_request_status", label: "Check 311 request status", description: "'What's the status of my pothole report?' — AI looks up the citizen's service request by record number and reports current status in plain language." },
  { group: "311 / Citizen Services", name: "accela_search_service_requests_by_address", label: "Search 311 by address", description: "Find all open 311 service requests near a given address. Shows whether the issue is already being tracked so the citizen doesn't duplicate a report." },
  { group: "311 / Citizen Services", name: "accela_get_service_request_types", label: "Get 311 issue categories", description: "Retrieve the list of available 311 issue types configured for the agency (Pothole, Graffiti, Street Light, etc.) — helps citizen pick the right category." },
  { group: "311 / Citizen Services", name: "accela_get_fees", label: "Get permit fees", description: "Retrieve the fee schedule for a permit application — total due, paid, and outstanding balance. Citizens can ask 'how much do I owe on my permit?'" },
  { group: "311 / Citizen Services", name: "accela_add_record_comment", label: "Add comment to record", description: "Citizen adds additional information or an update to an existing permit or service request — e.g. 'the pothole got worse' or 'I have more details about my permit application'." },
];

const GROUP_ICONS: Record<string, string> = {
  "Records": "📋",
  "Inspections": "🔍",
  "Contacts": "👤",
  "Agency & Lookup": "🏛️",
  "311 / Citizen Services": "🏙️"
};

const COMING_SOON_TOOLS: { name: string; label: string; description: string }[] = [];

// ─── How it works card ────────────────────────────────────────────────────────

function HowItWorksCard() {
  return (
    <div className="bg-white rounded-2xl shadow-md p-6 mb-4">
      <div className="font-semibold text-slate-800 text-base mb-1">How Accela Civic works in Velaro</div>
      <p className="text-xs text-slate-500 mb-4">Three steps — connect once, every citizen services workflow activates.</p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* Step 1 */}
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 relative">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-slate-800 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">1</span>
            <span className="font-semibold text-slate-700 text-sm">Connect your agency</span>
          </div>
          <p className="text-xs text-slate-500 mb-3">Authorize with your Accela Client ID and Secret. The OAuth popup handles the rest.</p>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <span className="font-mono bg-white border border-slate-200 px-1.5 py-0.5 rounded text-[10px]">Client ID</span>
              <span className="text-slate-400">from Accela Marketplace</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <span className="font-mono bg-white border border-slate-200 px-1.5 py-0.5 rounded text-[10px]">Agency Name</span>
              <span className="text-slate-400">your Accela agency code</span>
            </div>
          </div>
          <div className="hidden sm:flex absolute -right-3.5 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white border border-slate-200 shadow items-center justify-center">
            <svg viewBox="0 0 10 10" width="10" height="10" fill="none">
              <path d="M3 2L7 5L3 8" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
        </div>

        {/* Step 2 */}
        <div className="rounded-xl border border-green-200 bg-green-50 p-4 relative">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-green-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
            <span className="font-semibold text-green-800 text-sm">AI skills go live</span>
          </div>
          <p className="text-xs text-green-700 mb-3">30 tools activate immediately — permits, 311, inspections, contacts, licensing, and parcel data. The AI calls them based on what citizens ask.</p>
          <div className="grid grid-cols-2 gap-1">
            {["Permit lookup","Inspection schedule","License verify","311 submit","Status check","Parcel info","Contact search","Workflow status","Document list","Condition check","Record creation","Agency info","Contractor verify","Inspector booking"].map((t) => (
              <div key={t} className="flex items-center gap-1 text-[10px] text-green-700">
                <svg viewBox="0 0 8 8" width="8" height="8" fill="none" className="flex-shrink-0">
                  <path d="M1 4L3 6L7 2" stroke="#16a34a" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                {t}
              </div>
            ))}
          </div>
          <div className="hidden sm:flex absolute -right-3.5 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white border border-slate-200 shadow items-center justify-center">
            <svg viewBox="0 0 10 10" width="10" height="10" fill="none">
              <path d="M3 2L7 5L3 8" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
        </div>

        {/* Step 3 */}
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">3</span>
            <span className="font-semibold text-blue-800 text-sm">Citizens self-serve 24/7</span>
          </div>
          <p className="text-xs text-blue-700 mb-3">Data surfaces in 3 places — AI, agent sidebar, and workflow variables.</p>
          <div className="space-y-2">
            <div className="bg-white rounded-lg p-2 border border-blue-100">
              <div className="text-[10px] font-semibold text-blue-800 uppercase tracking-wide">Chat widget</div>
              <div className="text-[10px] text-blue-600 mt-0.5">Citizens check permit status, book inspections, or report issues without calling the agency</div>
            </div>
            <div className="bg-white rounded-lg p-2 border border-blue-100">
              <div className="text-[10px] font-semibold text-blue-800 uppercase tracking-wide">Agent sidebar</div>
              <div className="text-[10px] text-blue-600 mt-0.5">Agents see permit records, inspection history, and contact details pulled live from Accela</div>
            </div>
            <div className="bg-white rounded-lg p-2 border border-blue-100">
              <div className="text-[10px] font-semibold text-blue-800 uppercase tracking-wide">Workflow variables</div>
              <div className="text-[10px] text-blue-600 mt-0.5">Map Accela fields to HTTP POST variables for pushing data to other civic systems</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Capabilities panel ───────────────────────────────────────────────────────

function CapabilitiesPanel() {
  const [open, setOpen] = useState(false);
  const [openSections, setOpenSections] = useState<Set<number>>(new Set([0]));

  function toggleSection(i: number) {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  }

  return (
    <div className="bg-white rounded-2xl shadow-md mb-4">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-slate-50 rounded-2xl"
        aria-expanded={open}
      >
        <div className="flex items-center gap-3">
          <span className="font-semibold text-slate-800">What's included</span>
          <span className="text-xs bg-green-100 text-green-700 font-medium px-2 py-0.5 rounded-full">
            {CAPABILITIES.length} feature areas
          </span>
          {COMING_SOON_TOOLS.length > 0 && (
            <span className="text-xs bg-amber-100 text-amber-700 font-medium px-2 py-0.5 rounded-full">
              {COMING_SOON_TOOLS.length} coming soon
            </span>
          )}
        </div>
        <svg
          className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ${open ? "rotate-180" : ""}`}
          viewBox="0 0 16 16" fill="currentColor"
        >
          <path d="M8 10.5L3 5.5H13L8 10.5Z" />
        </svg>
      </button>

      {open && (
        <div className="border-t border-slate-100 px-6 pb-6">
          <div className="mt-4 rounded-lg bg-green-50 border border-green-200 p-3 mb-4 text-xs text-green-800">
            <strong>AI tools work without any configuration.</strong> Connect your Accela agency (OAuth) and the AI
            can immediately look up permits, licenses, inspections, contacts, and parcel data during any conversation.
            No JSON, no field mapping required — the AI orchestrates tool calls automatically.
          </div>

          <div className="space-y-2">
            {CAPABILITIES.map((cap, i) => (
              <div key={i} className="rounded-lg border border-slate-100 overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleSection(i)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50"
                  aria-expanded={openSections.has(i)}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-base">{cap.icon}</span>
                    <span className="text-sm font-medium text-slate-700">{cap.title}</span>
                  </div>
                  <svg
                    className={`w-3.5 h-3.5 text-slate-400 transition-transform flex-shrink-0 ${openSections.has(i) ? "rotate-180" : ""}`}
                    viewBox="0 0 16 16" fill="currentColor"
                  >
                    <path d="M8 10.5L3 5.5H13L8 10.5Z" />
                  </svg>
                </button>
                {openSections.has(i) && (
                  <div className="px-4 pb-3 space-y-1.5 border-t border-slate-50">
                    {cap.items.map((item, j) => (
                      <div key={j} className="flex items-start gap-2 pt-1.5">
                        <div className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 bg-green-500" />
                        <span className="text-xs text-slate-600">{item}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-4 rounded-lg bg-blue-50 border border-blue-200 p-4 text-xs text-blue-800">
            <div className="font-semibold mb-1">Government-grade AI orchestration</div>
            <p className="mb-2">
              The Accela AI node automatically queries your agency's Accela instance as part of citizen conversations.
              Use the <strong>Permit Status Bot</strong> or <strong>311 Service Request</strong> templates
              to deploy a dedicated Accela AI that handles the most common citizen inquiries automatically.
            </p>
            <p>
              Field mapping and HTTP POST nodes are optional — they're useful for pushing permit data to
              your CRM, ticketing, or GIS system after the conversation. The AI handles the citizen interaction
              without any extra config.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function AccelaCivic() {
  const api = useApi();
  const { teamId, updateTeamId } = useSelectedTeam();
  const [configs, setConfigs] = useState<AccelaConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedConfigId, setSelectedConfigId] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<"setup" | "data-mapping" | "logs">("setup");
  const [showWizard, setShowWizard] = useState(false);
  const [activeGroup, setActiveGroup] = useState<string>("Records");

  const fetchConfigs = useCallback(async () => {
    try {
      const resp = await api.messaging.get("Accela/Integrations/");
      const data = await resp.json();
      const now = new Date();
      const mapped = (Array.isArray(data) ? data : []).map((x: any) => ({
        id: x.id,
        agencyName: x.agencyName ?? "",
        clientId: x.clientId ?? "",
        clientSecret: x.clientSecret ?? "",
        emailAddress: x.emailAddress ?? "",
        accessToken: x.accessToken ?? "",
        refreshToken: x.refreshToken ?? "",
        teamId: x.teamId ?? 0,
        token: x.accessToken ?? "",
        refreshTokenExpired: x.refreshTokenExpireAt ? new Date(x.refreshTokenExpireAt) < now : false
      }));
      setConfigs(mapped);
      if (mapped.length === 1) setSelectedConfigId(mapped[0].id);
    } catch {
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

  const addConfig = useCallback(() => { fetchConfigs(); }, [fetchConfigs]);

  const deleteConfig = useCallback(async (id: number) => {
    setConfigs((prev) => prev.filter((x) => x.id !== id));
    try { await api.messaging.delete(`Accela/Integrations/${id}`); } catch { /* already removed from state */ }
  }, [api.messaging]);

  const handleConfigChange = (id: number, field: keyof AccelaConfig, value: string) => {
    setConfigs((prev) => prev.map((c) => c.id === id ? { ...c, [field]: value } : c));
  };

  const updateConfig = useCallback((id: number, updated: Partial<AccelaConfig>) => {
    setConfigs((prev) => prev.map((c) => c.id === id ? { ...c, ...updated } : c));
  }, []);

  const hasActiveConfig = configs.length > 0;
  const groups = [...new Set(AI_TOOLS.map((t) => t.group))];
  const visibleTools = AI_TOOLS.filter((t) => t.group === activeGroup);

  return (
    <div>
      {showWizard && (
        <StarterWorkflowWizard
          integration="accela"
          accelaConfigId={configs.find((c) => c.id === selectedConfigId)?.id ?? configs[0]?.id}
          onClose={() => setShowWizard(false)}
        />
      )}

      {/* Tabs */}
      <div className="flex border-b border-slate-200 mb-4">
        {(["setup", "data-mapping", "logs"] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            className={`py-2 mr-4 text-sm cursor-pointer ${activeTab === tab ? "border-b-2 border-primary text-primary font-medium" : "text-slate-500"}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === "setup" ? "Setup" : tab === "data-mapping" ? "Data Mapping" : "Activity Log"}
          </button>
        ))}
      </div>

      {hasActiveConfig && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 flex flex-col gap-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-blue-800 mb-0.5">
                Deploy the chat widget to your site
              </div>
              <div className="text-xs text-blue-600">
                Your Accela Civic integration is connected — grab your embed code from Deployments to go live.
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <a href="/chat_window" className="text-xs px-3 py-1.5 rounded-lg bg-white border border-blue-300 text-blue-700 hover:bg-blue-100 transition">Chat Window</a>
              <a href="/deployments" className="text-xs px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition">Get Embed Code →</a>
            </div>
          </div>
          <div className="flex items-start gap-2 bg-white/60 rounded-lg px-3 py-2 text-xs text-blue-700 border border-blue-100">
            <i className="fa-solid fa-lightbulb mt-0.5 shrink-0" />
            <span>
              <strong>Tip:</strong> You can also create a <strong>Knowledge Base Q&A</strong> page widget
              from <a href="/deployments" className="underline hover:text-blue-900">Deployments → Page Widgets</a> for
              a lightweight, AI-only Q&A experience — no live agent needed.
            </span>
          </div>
        </div>
      )}

      {/* ── Activity Log tab ── */}
      {activeTab === "logs" && (
        <div className="bg-white rounded-2xl shadow-md p-6">
          <IntegrationActivityLog integrationSlug="Accela" />
        </div>
      )}

      {/* ── Data Mapping tab ── */}
      {activeTab === "data-mapping" && (
        <div className="bg-white rounded-2xl shadow-md p-6">
          <div className="font-semibold text-slate-800 text-lg mb-1">Data Mapping</div>
          <p className="text-sm text-slate-500 mb-5">
            Configure which Accela fields appear in the agent sidebar and become available as HTTP POST variables.
            The AI works without this — mapping is optional and additive.
          </p>
          <AccelaDataMapping
            teamId={teamId!}
            selectedConfigId={selectedConfigId}
          />
        </div>
      )}

      {/* ── Setup tab ── */}
      {activeTab === "setup" && (
        <div>
          <HowItWorksCard />
          <CapabilitiesPanel />

          {loading ? (
            <div className="flex justify-center py-12"><LoadingSpinner /></div>
          ) : (
            <>
              <div className="mb-6">
                <AccelaAccounts
                  configs={configs}
                  deleteConfig={deleteConfig}
                  addConfig={addConfig}
                  teamId={teamId}
                  onChange={handleConfigChange}
                  onSelectConfig={setSelectedConfigId}
                  updateConfig={updateConfig}
                  refreshConfigs={fetchConfigs}
                />
              </div>

              {/* AI Tools panel */}
              <div className="bg-white rounded-2xl shadow-md p-6 mb-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="font-semibold text-slate-800 text-lg flex items-center gap-2">
                      AI Tools
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${hasActiveConfig ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                        {hasActiveConfig ? "Active" : "No active connection"}
                      </span>
                    </div>
                    <div className="text-sm text-slate-500 mt-0.5">
                      30 tools available — permits, inspections, contacts, licensing, parcel lookup, and 311 citizen service requests (the full PublicStuff feature set).
                      Connect your agency and they activate immediately.
                    </div>
                  </div>
                  {hasActiveConfig && (
                    <button
                      type="button"
                      onClick={() => setShowWizard(true)}
                      className="text-sm px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 whitespace-nowrap font-medium shadow-sm"
                    >
                      Launch Starter Wizard
                    </button>
                  )}
                </div>

                {/* Group tabs */}
                <div className="flex gap-2 mb-4 flex-wrap">
                  {groups.map((g) => (
                    <button
                      key={g}
                      type="button"
                      onClick={() => setActiveGroup(g)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        activeGroup === g ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                    >
                      <span>{GROUP_ICONS[g] ?? "🔧"}</span>
                      <span>{g}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${activeGroup === g ? "bg-slate-700 text-slate-300" : "bg-slate-200 text-slate-500"}`}>
                        {AI_TOOLS.filter((t) => t.group === g).length}
                      </span>
                    </button>
                  ))}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                  {visibleTools.map((tool) => (
                    <div
                      key={tool.name}
                      className={`flex gap-3 p-3 rounded-lg border ${hasActiveConfig ? "border-green-200 bg-green-50" : "border-slate-200 bg-slate-50"}`}
                    >
                      <div className={`mt-0.5 w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center ${hasActiveConfig ? "bg-green-500" : "bg-slate-300"}`}>
                        {hasActiveConfig && (
                          <svg viewBox="0 0 10 10" width="8" height="8" fill="none">
                            <path d="M1.5 5L3.5 7L8.5 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-700">{tool.label}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{tool.description}</div>
                        <code className="text-[10px] text-slate-400 mt-1 block">{tool.name}</code>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
                  <strong>How the AI uses these tools:</strong> When a citizen asks "what's the status of my building permit?",
                  the AI calls <code className="bg-blue-100 px-1 rounded">accela_search_records</code> with the address or applicant name,
                  reads the permit status from Accela, and replies in natural language — no manual lookup, no hold music,
                  no after-hours voicemail. Available 24/7.
                </div>

                <div className="mt-8 pt-8 border-t border-slate-200">
                  <SkillControlsPanel integration="accela" skills={AI_TOOLS} />
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function BackToIntegrationLanding() {
  const navigate = useNavigate();
  return (
    <div className="flex cursor-pointer" onClick={() => navigate("/integrations")}>
      <IconClose1 className="h-6 w-6" />
    </div>
  );
}
